/**
 * The stub the merchant pastes into their storefront.
 *
 * This is the only code we cannot update without the merchant touching their
 * theme, so it must stay tiny and must never change shape. Everything it does
 * is defer: it resolves configuration, then fetches the real bundle from CDN.
 * That indirection is what lets us ship widget updates to every merchant
 * without asking any of them to redeploy.
 */

interface CheckoutConfig {
    key: string;
    cdn?: string;
    api?: string;
}

declare global {
    interface Window {
        Checkout?: {
            config?: CheckoutConfig;
            open?: (cartId?: string) => void;
            _q?: Array<[string, unknown]>;
        };
    }
}

(function bootstrap() {
    const w = window;
    const existing = w.Checkout;

    if (!existing?.config?.key) {
        console.error('[checkout] Missing publishable key.');
        return;
    }

    const cdn = existing.config.cdn ?? 'https://cdn.checkout.example';

    // Calls made before the bundle lands are queued rather than dropped. A buyer
    // who taps "Buy now" during the few hundred milliseconds the script is in
    // flight is exactly the buyer we cannot afford to lose.
    const queue: Array<[string, unknown]> = existing._q ?? [];
    w.Checkout = {
        ...existing,
        _q: queue,
        open: (cartId?: string) => queue.push(['open', cartId]),
    };

    // Warm the connections the checkout will need before it needs them.
    // Do not set crossOrigin on these: a cross-origin preconnect with
    // anonymous mode requires ACAO on a resource we are only warming.
    for (const href of [cdn, existing.config.api]) {
        if (!href) continue;
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = href;
        document.head.appendChild(link);
    }

    // Classic <script> tags may load cross-origin without CORS headers.
    // Setting crossOrigin="anonymous" would require Access-Control-Allow-Origin
    // on checkout.js, and static files under public/ are served without the
    // Laravel CORS middleware — which is exactly the WooCommerce failure mode.
    const script = document.createElement('script');
    script.src = `${cdn}/widget/checkout.js`;
    script.async = true;
    script.onerror = () => console.error('[checkout] Failed to load checkout bundle.');
    document.head.appendChild(script);
})();

export {};
